import React, { useState, useEffect } from 'react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import { 
  DollarSign, Clock, CheckCircle, AlertCircle, X, RefreshCw, FileText,
  Download, Eye, CreditCard, Wallet, TrendingUp, ArrowUpRight, ArrowDownLeft,
  Calendar, Filter, Search, Send, Sparkles, Home, Heart, Building2, 
  Activity, MoreVertical, ExternalLink, Plus
} from 'lucide-react';

export default function PaymentManagement({ onClose, onOpenAIJobPosting, onOpenAdminDashboard, onOpenPreferredWorkers }) {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('history');
  const [payments, setPayments] = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);
  const [escrowBalance, setEscrowBalance] = useState(0);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    date_range: '30'
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');

  useEffect(() => {
    loadPaymentData();
  }, [filters]);

  // Stripe wallet top-up return handling: ?stripe_session_id=...&flow=wallet_topup
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('stripe_session_id');
    if (!sid || params.get('flow') !== 'wallet_topup') return;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await apiCall(`/api/payments/stripe/status/${sid}`);
        if (res.payment_status === 'paid') {
          toast.success('Wallet funded!', { description: 'Your card payment was received and added to your wallet.' });
          loadPaymentData();
          return;
        }
        if (['failed', 'expired', 'canceled'].includes(res.payment_status)) {
          toast.error('Payment not completed', { description: `Stripe reported: ${res.payment_status}` });
          return;
        }
      } catch { /* transient */ }
      if (attempts < 8) setTimeout(poll, 2500);
    };
    poll();
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  const loadPaymentData = async () => {
    setLoading(true);
    try {
      const [paymentsRes, statsRes, walletRes] = await Promise.all([
        apiCall(`/api/employer/payments?status=${filters.status}&days=${filters.date_range}`),
        apiCall('/api/employer/payment-stats'),
        apiCall('/api/employer/wallet')
      ]);
      
      setPayments(paymentsRes.payments || []);
      setEscrowBalance(paymentsRes.escrow_balance || 0);
      setStats(statsRes);
      setWalletBalance(walletRes.balance || 0);
      
      // Set recent activity from payments
      setRecentActivity(paymentsRes.payments?.slice(0, 5) || []);
    } catch (error) {
      console.error('Failed to load payment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTopUp = async () => {
    const amount = parseFloat(topUpAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    try {
      await apiCall('/api/employer/wallet/topup', {
        method: 'POST',
        body: JSON.stringify({ amount })
      });
      toast.success(`$${amount.toFixed(2)} added to your wallet!`);
      setShowTopUpModal(false);
      setTopUpAmount('');
      loadPaymentData();
    } catch (error) {
      toast.error(error.message || 'Failed to top up wallet');
    }
  };

  const handleReleasePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to release this payment?')) return;

    try {
      await apiCall(`/api/employer/payments/${paymentId}/release`, {
        method: 'POST'
      });
      toast.success('Payment released successfully');
      loadPaymentData();
    } catch (error) {
      toast.error(error.message || 'Failed to release payment');
    }
  };

  const handleDownloadInvoice = async (paymentId) => {
    try {
      const response = await apiCall(`/api/employer/payments/${paymentId}/invoice`);
      // Create download link
      const blob = new Blob([response.invoice_data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${paymentId}.pdf`;
      a.click();
      toast.success('Invoice downloaded');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      pending: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Pending' },
      approved: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Approved' },
      released: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Released' },
      completed: { bg: 'bg-green-100', text: 'text-green-700', label: 'Completed' },
      failed: { bg: 'bg-red-100', text: 'text-red-700', label: 'Failed' },
      disputed: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Disputed' }
    };
    const config = configs[status] || configs.pending;
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <DollarSign className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Payment Management</h2>
                <p className="text-white/80">Track payments, invoices, and escrow</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl transition">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Quick Actions Bar */}
        <div className="bg-slate-50 border-b border-slate-200 p-4">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => { onClose(); onOpenAIJobPosting?.(); }}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:shadow-lg transition"
            >
              <Sparkles className="w-5 h-5" />
              Post Job with AI
            </button>
            <button
              onClick={() => { onClose(); onOpenAdminDashboard?.(); }}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition"
            >
              <Home className="w-5 h-5" />
              Admin Dashboard
            </button>
            <button
              onClick={() => { onClose(); onOpenPreferredWorkers?.(); }}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl font-semibold hover:shadow-lg transition"
            >
              <Heart className="w-5 h-5" />
              Preferred Workers
            </button>
            <button
              className="flex items-center gap-2 px-5 py-2.5 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition"
            >
              <Building2 className="w-5 h-5" />
              Organization
            </button>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[calc(95vh-200px)]">
          {/* Stats Cards Section */}
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-slate-900">Payment Management</h3>
                <p className="text-slate-500">Track payments, invoices, and escrow</p>
              </div>
              <button
                onClick={() => setActiveTab('history')}
                className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition"
              >
                <FileText className="w-5 h-5" />
                View All Payments
              </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-lg transition relative">
                <div className="flex items-start justify-between mb-3">
                  <div className="p-3 bg-emerald-100 rounded-xl">
                    <Wallet className="w-6 h-6 text-emerald-600" />
                  </div>
                  <button
                    onClick={() => setShowTopUpModal(true)}
                    className="px-3 py-1.5 bg-emerald-500 text-white text-sm font-semibold rounded-lg hover:bg-emerald-600 transition flex items-center gap-1"
                    data-testid="top-up-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Top Up
                  </button>
                </div>
                <p className="text-3xl font-bold text-slate-900">${walletBalance.toFixed(2)}</p>
                <p className="text-slate-500 mt-1">Wallet Balance</p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-lg transition">
                <div className="p-3 bg-amber-100 rounded-xl w-fit mb-3">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
                <p className="text-3xl font-bold text-slate-900">${escrowBalance.toFixed(2)}</p>
                <p className="text-slate-500 mt-1">In Escrow</p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-lg transition">
                <div className="p-3 bg-blue-100 rounded-xl w-fit mb-3">
                  <DollarSign className="w-6 h-6 text-blue-600" />
                </div>
                <p className="text-3xl font-bold text-slate-900">${stats?.total_spent?.toFixed(2) || '0'}</p>
                <p className="text-slate-500 mt-1">Total Spent</p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-lg transition">
                <div className="p-3 bg-purple-100 rounded-xl w-fit mb-3">
                  <CheckCircle className="w-6 h-6 text-purple-600" />
                </div>
                <p className="text-3xl font-bold text-slate-900">{stats?.completed_payments || 0}</p>
                <p className="text-slate-500 mt-1">Completed Payments</p>
              </div>
            </div>
          </div>

          {/* Recent Activity Section */}
          <div className="p-6 border-b border-slate-200">
            <h4 className="text-lg font-bold text-slate-900 mb-4">Recent Activity</h4>
            {recentActivity.length === 0 ? (
              <div className="text-center py-8 bg-slate-50 rounded-xl">
                <Activity className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No recent activity</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentActivity.map((activity, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${
                        activity.status === 'completed' ? 'bg-emerald-100' : 
                        activity.status === 'pending' ? 'bg-amber-100' : 'bg-blue-100'
                      }`}>
                        {activity.status === 'completed' ? (
                          <ArrowUpRight className="w-5 h-5 text-emerald-600" />
                        ) : activity.status === 'pending' ? (
                          <Clock className="w-5 h-5 text-amber-600" />
                        ) : (
                          <DollarSign className="w-5 h-5 text-blue-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{activity.job_title || 'Payment'}</p>
                        <p className="text-sm text-slate-500">
                          {activity.worker_name} • {new Date(activity.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-slate-900">${activity.amount?.toFixed(2) || '0.00'}</p>
                      {getStatusBadge(activity.status)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tabs Section */}
          <div className="border-b border-gray-200">
            <div className="flex gap-1 p-2">
              {[
                { id: 'history', label: 'Payment History', icon: <FileText className="w-4 h-4" /> },
                { id: 'pending', label: 'Pending Release', icon: <Clock className="w-4 h-4" /> },
                { id: 'invoices', label: 'Invoices', icon: <Download className="w-4 h-4" /> }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition ${
                    activeTab === tab.id
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Filters */}
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <div className="flex gap-4 items-center">
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition appearance-none cursor-pointer"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '16px', paddingRight: '40px' }}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="released">Released</option>
                <option value="completed">Completed</option>
              </select>
              <select
                value={filters.date_range}
                onChange={(e) => setFilters({ ...filters, date_range: e.target.value })}
                className="px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition appearance-none cursor-pointer"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '16px', paddingRight: '40px' }}
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="365">Last year</option>
              </select>
              <button
                onClick={loadPaymentData}
                className="px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 text-emerald-600 animate-spin" />
              </div>
            ) : payments.length === 0 ? (
              <div className="text-center py-12">
                <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Payments Found</h3>
                <p className="text-gray-500">Payments will appear here once jobs are completed.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {payments.map((payment) => (
                  <div 
                    key={payment._id}
                    className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-xl ${
                          payment.status === 'completed' || payment.status === 'released'
                            ? 'bg-emerald-100'
                            : payment.status === 'pending'
                            ? 'bg-amber-100'
                            : 'bg-gray-100'
                        }`}>
                          {payment.status === 'completed' || payment.status === 'released' ? (
                            <ArrowUpRight className="w-6 h-6 text-emerald-600" />
                          ) : (
                            <Clock className="w-6 h-6 text-amber-600" />
                          )}
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">{payment.job_title}</h4>
                          <p className="text-sm text-gray-500">{payment.worker_name}</p>
                          <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {new Date(payment.created_at).toLocaleDateString()}
                            </span>
                            <span>Invoice #{payment.invoice_number || payment._id.slice(-6)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-900">${payment.amount.toFixed(2)}</p>
                        <div className="mt-2">
                          {getStatusBadge(payment.status)}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100">
                      {payment.status === 'approved' && (
                        <button
                          onClick={() => handleReleasePayment(payment._id)}
                          className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition flex items-center gap-2"
                        >
                          <Send className="w-4 h-4" />
                          Release Payment
                        </button>
                      )}
                      <button
                        onClick={() => handleDownloadInvoice(payment._id)}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition flex items-center gap-2"
                      >
                        <Download className="w-4 h-4" />
                        Invoice
                      </button>
                      <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        Details
                      </button>
                    </div>

                    {/* Payment Timeline */}
                    {payment.timeline && payment.timeline.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <p className="text-sm font-medium text-gray-500 mb-2">Payment Timeline</p>
                        <div className="flex items-center gap-2 text-xs">
                          {payment.timeline.map((event, idx) => (
                            <React.Fragment key={idx}>
                              <span className={`px-2 py-1 rounded ${
                                event.completed ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
                              }`}>
                                {event.label}
                              </span>
                              {idx < payment.timeline.length - 1 && (
                                <span className="text-gray-300">→</span>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top Up Wallet Modal */}
      {showTopUpModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white">Top Up Wallet</h3>
                <button
                  onClick={() => {
                    setShowTopUpModal(false);
                    setTopUpAmount('');
                  }}
                  className="p-2 hover:bg-white/20 rounded-full transition"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Amount ($)
                </label>
                <input
                  type="number"
                  value={topUpAmount}
                  onChange={(e) => setTopUpAmount(e.target.value)}
                  placeholder="e.g., 500"
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-lg"
                  min="1"
                  step="0.01"
                  data-testid="top-up-amount-input"
                />
              </div>
              
              {/* Quick Amount Buttons */}
              <div className="mb-6">
                <p className="text-sm text-slate-500 mb-3">Quick amounts:</p>
                <div className="grid grid-cols-4 gap-2">
                  {[100, 250, 500, 1000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setTopUpAmount(amount.toString())}
                      className={`py-2.5 rounded-xl font-semibold transition ${
                        topUpAmount === amount.toString()
                          ? 'bg-emerald-100 text-emerald-700 border-2 border-emerald-500'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                      data-testid={`quick-amount-${amount}`}
                    >
                      ${amount}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowTopUpModal(false);
                    setTopUpAmount('');
                  }}
                  className="flex-1 px-4 py-3 border border-slate-300 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleTopUp}
                  disabled={!topUpAmount || parseFloat(topUpAmount) <= 0}
                  className="flex-1 px-4 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="add-funds-btn"
                >
                  <Plus className="w-5 h-5" />
                  Pay with Card (Stripe)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
